import {naturalSortByKey, objectFromEntries} from '@benchling/js-utils';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import TemplateChangelog from 'config-migration/import/templates-step/TemplateChangelog';
import ProcessTaskSchemaPlanSection from 'config-migration/import/workflows-step/ProcessTaskSchemaPlanSection';
import {TemplateAttributes, UnitAttributes} from 'config-migration/MigrationConstants';
import {PrimaryMigrationObject, ObjectWithAttributes} from 'config-migration/types/MigrationObjects';
import {
  PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT,
  PrimaryObjectType,
  SecondaryObjectType,
} from 'config-migration/types/ObjectType';
import lazyInit from 'util/lazyInit';

import {MigrationStep, PlannableMigrationStep} from '../types/MigrationStep';
import {ImportChangelogSection, ImportChangelogSectionProps} from './import-changelog/ImportChangelog';
import GrouperPlanSection from './import-plan-sections/GrouperPlanSection';
import ImportPlanSection, {ImportPlanSectionProps} from './import-plan-sections/ImportPlanSection';
import {isTemplatePlannedOnStep} from './planning-logic/templateFiltering';

/**
 * Whether `PrimaryObject`s should be mapped based on their name during planning.
 *
 * During planning (if no provenance id is available), we let the user decide what destination object to map
 *  their source object to. Source objects in this list should be mapped to destination objects with matching
 *  names by default.
 *
 * Note: `ChildObject`s automatically default to name-matching.
 */
export const DEFAULT_NAME_MATCHED_OBJECT_TYPES = ['TemplateCollection', 'UnitType'];

/** The tabs that will be rendered in the ImportWizard's step UI. */
export const MIGRATION_PLAN_TABS = [
  {
    migrationStep: MigrationStep.TENANT_SETTINGS,
    title: 'Tenant Settings',
    isEnabled: (): boolean => BenchlingConfig.UNITS_ENABLE_CONFIG_MIGRATION,
  },
  {
    migrationStep: MigrationStep.SCHEMAS,
    title: 'Schemas',
    isEnabled: (): true => true,
  },
  {
    migrationStep: MigrationStep.TEMPLATES,
    title: 'Templates',
    isEnabled: (): true => true,
  },
  {
    migrationStep: MigrationStep.PROCESS_TASK_SCHEMAS,
    title: 'Workflows',
    isEnabled: (): boolean => BenchlingConfig.WORKFLOWS_ENABLE,
  },
  {
    migrationStep: MigrationStep.STUDIES,
    title: 'Studies',
    isEnabled: (): boolean => BenchlingConfig.STUDIES_ENABLE,
  },
];

export const ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP: Record<
  PlannableMigrationStep,
  Array<PrimaryObjectType>
> = {
  [MigrationStep.TENANT_SETTINGS]: [PrimaryObjectType.UnitType, PrimaryObjectType.Unit],
  [MigrationStep.SCHEMAS]: [
    PrimaryObjectType.Dropdown,
    PrimaryObjectType.EntitySchema,
    PrimaryObjectType.BatchSchema,
    PrimaryObjectType.LocationSchema,
    PrimaryObjectType.BoxSchema,
    PrimaryObjectType.PlateSchema,
    PrimaryObjectType.ContainerSchema,
    PrimaryObjectType.RunSchema,
    PrimaryObjectType.ResultSchema,
    PrimaryObjectType.Fieldset,
    PrimaryObjectType.TestDefinition,
  ],
  [MigrationStep.TEMPLATES]: [
    PrimaryObjectType.TemplateCollection,
    // Note: We need to exclude Execution Templates i.e. Templates w/`processTaskSchemaId` in their
    //  objectAttributes
    PrimaryObjectType.Template,
    PrimaryObjectType.Subtemplate,
    PrimaryObjectType.Procedure,
    PrimaryObjectType.AnalysisTemplate,
  ],
  [MigrationStep.PROCESS_TASK_SCHEMAS]: [
    PrimaryObjectType.ProcessTaskSchema,
    PrimaryObjectType.ProcessOutputSchema,
    PrimaryObjectType.ProcessFlowchartConfigVersion,
    // Note: We only want to include Execution Templates i.e. Templates w/`processTaskSchemaId` in their
    //  objectAttributes
    PrimaryObjectType.Template,
    PrimaryObjectType.RequestV2Definition,
  ],
  [MigrationStep.STUDIES]: [PrimaryObjectType.StudySchema],
};

/**
 * Object types that remove children instead of archiving them. The mapping values are the children that
 * are removed, in the case where the parent object has multiple child types.
 *
 * TODO(BNCH-100932): Return whether the child is removed or archived instead of general "Subtraction" action
 */
export const OBJECT_TYPE_TO_REMOVED_CHILD_CLASS: Map<PrimaryObjectType | SecondaryObjectType, string> =
  new Map([[PrimaryObjectType.Fieldset, 'SchemaField']]);

/**
 * TODO(BNCH-65943): We only use keep logic on things outside of this list - because the change log is not
 *  reliable for templates and subtemplates due to gaps with the BE's diffing logic. Templates currently don't
 *  have keep logic, they only have Unmatched.
 */
export const KEPT_OBJECT_TYPE_IGNORELIST = [
  PrimaryObjectType.Subtemplate,
  PrimaryObjectType.Template,
  PrimaryObjectType.TemplateCollection,
];

/**
 * Object types that can be grouped by template collection.
 */

export const TEMPLATE_COLLECTIBLE_OBJECT_TYPES: Array<PrimaryObjectType> = [
  PrimaryObjectType.Template,
  PrimaryObjectType.Subtemplate,
  PrimaryObjectType.Procedure,
  PrimaryObjectType.AnalysisTemplate,
];

/**
 * An interface for the information needed to display the configuration options for a section
 * of objects with the same Object Type(s).
 *
 * This functions as a more abstract representation of the various sections of object types supported.
 *
 * E.g. used to
 *  - generate object type groups in ImportErrorLogs
 *  - combine with a set of MigrationObjectPlans to generate a MigrationPlanSection
 */
export interface ObjectSectionConfig {
  /** The object types to display in this section.  */
  objectTypes: Array<PrimaryObjectType>;
  /** How to generate the name of each section in the config pane. */
  displayNameFn: <T extends PrimaryMigrationObject>(objects: Array<T>) => string;
  /**
   * The component to use when displaying this section in the changelog. Unless you need custom logic,
   * use `ImportChangelogSection`.
   */
  changelogComponent: (props: ImportChangelogSectionProps) => JSX.Element;
  /** The component to use when displaying this section for planning in the ImportWizard. Unless you need
   * custom logic, use `ImportPlanSection`. */
  planComponent: (props: ImportPlanSectionProps) => JSX.Element;
  /** The function to use when grouping the objects within this section. This is generally done by sorting on
   * name or on the object types supported within the section. Generally sorting the objects on
   * `objectDisplayName` is sufficient.
   */
  groupRelevantObjects: <T extends PrimaryMigrationObject>(objects: Array<T>) => Array<Array<T>>;
  /** What tab this section should be displayed on. */
  migrationTab: MigrationStep;
  /** An optional function to return if an object that would be this section should be excluded anyway. */
  shouldExcludeObjectWithAttributes?: <T extends ObjectWithAttributes>(object: T) => boolean;
}

/* Create a separate section for one type of Schema from the SCHEMAS step */
const mapObjectTypeToObjectSectionConfig = (
  objectType: PrimaryObjectType,
  tab: MigrationStep
): ObjectSectionConfig => ({
  objectTypes: [objectType],
  displayNameFn: <T extends PrimaryMigrationObject>(_: Array<T>): string =>
    pluralize(2, PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[objectType]),
  changelogComponent: ImportChangelogSection,
  planComponent: ImportPlanSection,
  groupRelevantObjects: <T extends PrimaryMigrationObject>(objects: Array<T>) => [
    naturalSortByKey(objects, 'objectDisplayName'),
  ],
  migrationTab: tab,
});

// We lazy-init this due to circular dependencies within the objects this references.
// In tests, this can lead to imports being referenced before they're available.
/* Create one section all units in the TENANT_SETTINGS step. */
export const getSortedObjectSectionsForUnits = lazyInit<ObjectSectionConfig>(() => ({
  objectTypes: [PrimaryObjectType.UnitType, PrimaryObjectType.Unit],
  displayNameFn: <T extends PrimaryMigrationObject>(_: Array<T>): string =>
    pluralize(2, PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[PrimaryObjectType.Unit]),
  changelogComponent: ImportChangelogSection,
  planComponent: GrouperPlanSection,
  groupRelevantObjects: <T extends PrimaryMigrationObject>(objects: Array<T>) => {
    const unitsByTypeId = _.groupBy(
      objects.filter((object) => object.objectType === PrimaryObjectType.Unit),
      (object) => (object.objectAttributes as UnitAttributes).unitTypeId
    );

    return naturalSortByKey(
      objects.filter((object) => object.objectType === PrimaryObjectType.UnitType),
      'objectDisplayName'
    ).map((unitType) => [
      unitType,
      ...naturalSortByKey(unitsByTypeId[unitType.objectId] || [], 'objectDisplayName'),
    ]);
  },
  migrationTab: MigrationStep.TENANT_SETTINGS,
}));

// We lazy-init this due to circular dependencies within the objects this references.
// In tests, this can lead to imports being referenced before they're available.
/* Create one section for all objects used in the TEMPLATES step. */
export const getSortedObjectSectionsForTemplates = lazyInit<ObjectSectionConfig>(() => ({
  objectTypes: ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP[MigrationStep.TEMPLATES],
  displayNameFn: <T extends PrimaryMigrationObject>(objects: Array<T>): string => {
    const templateCollectionObject = objects.find(
      (object) => object.objectType === PrimaryObjectType.TemplateCollection
    )!;
    return templateCollectionObject.objectDisplayName;
  },
  changelogComponent: TemplateChangelog,
  planComponent: GrouperPlanSection,
  // Do not show Execution Templates within the Template pane - they are shown on the Workflows Pane
  shouldExcludeObjectWithAttributes: <T extends ObjectWithAttributes>(object: T) =>
    object.objectType === PrimaryObjectType.Template &&
    !isTemplatePlannedOnStep(object, MigrationStep.TEMPLATES),
  groupRelevantObjects: <T extends PrimaryMigrationObject>(objects: Array<T>) => {
    const templatesByCollectionId = _.groupBy(
      objects.filter((object) => TEMPLATE_COLLECTIBLE_OBJECT_TYPES.includes(object.objectType)),
      (object) => (object.objectAttributes as TemplateAttributes).templateCollectionId
    );

    return naturalSortByKey(
      objects.filter((object) => object.objectType === PrimaryObjectType.TemplateCollection),
      'objectDisplayName'
    ).map((collection) => [
      collection,
      ...naturalSortByKey(templatesByCollectionId[collection.objectId] || [], 'objectDisplayName'),
    ]);
  },
  migrationTab: MigrationStep.TEMPLATES,
}));

// We lazy-init this due to circular dependencies within the objects this references.
// In tests, this can lead to imports being referenced before they're available.
/* Create one section for all objects used in the PROCESS_TASK_SCHEMAS (Workflows) step. */
export const getSortedObjectSectionsForProcessTaskSchemas = lazyInit<ObjectSectionConfig>(() => ({
  objectTypes: ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP[MigrationStep.PROCESS_TASK_SCHEMAS],
  displayNameFn: <T extends PrimaryMigrationObject>(_: Array<T>): string =>
    `${pluralize(2, PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[PrimaryObjectType.ProcessTaskSchema])} and ${pluralize(2, PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[PrimaryObjectType.RequestV2Definition])}`,
  changelogComponent: ImportChangelogSection,
  planComponent: ProcessTaskSchemaPlanSection,
  // Do not show non-Execution Templates within the Workflows pane - they are shown on the Templates Pane
  shouldExcludeObjectWithAttributes: <T extends ObjectWithAttributes>(object: T) =>
    object.objectType === PrimaryObjectType.Template &&
    !isTemplatePlannedOnStep(object, MigrationStep.PROCESS_TASK_SCHEMAS),
  groupRelevantObjects: <T extends PrimaryMigrationObject>(objects: Array<T>) => [
    naturalSortByKey(objects, 'objectDisplayName'),
  ],
  migrationTab: MigrationStep.PROCESS_TASK_SCHEMAS,
}));

// We lazy-init this due to circular dependencies within the objects this references.
// In tests, this can lead to imports being referenced before they're available.
/* Create one section for all objects used in the StudySchema (Studies) step. */
export const getSortedObjectSectionsForStudies = lazyInit<ObjectSectionConfig>(() => ({
  objectTypes: ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP[MigrationStep.STUDIES],
  displayNameFn: <T extends PrimaryMigrationObject>(_: Array<T>): string =>
    pluralize(2, PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[PrimaryObjectType.StudySchema]),
  changelogComponent: ImportChangelogSection,
  planComponent: ImportPlanSection,
  groupRelevantObjects: <T extends PrimaryMigrationObject>(objects: Array<T>) => [
    naturalSortByKey(objects, 'objectDisplayName'),
  ],
  migrationTab: MigrationStep.STUDIES,
}));

// We lazy-init this array due to circular dependencies within the objects this references.
// In tests, this can lead to imports being referenced before they're available.
export const getSortedObjectSections = lazyInit<Array<ObjectSectionConfig>>(() => [
  getSortedObjectSectionsForUnits(),
  ...ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP[MigrationStep.SCHEMAS].map((objectType) =>
    mapObjectTypeToObjectSectionConfig(objectType, MigrationStep.SCHEMAS)
  ),
  getSortedObjectSectionsForTemplates(),
  getSortedObjectSectionsForProcessTaskSchemas(),
  getSortedObjectSectionsForStudies(),
]);

/**
 * We lazy-init this as it references the also lazily initialized getSortedObjectSections
 *
 * WARNING: This maps each objectType to MigrationStep, but certain types (e.g. Template) can belong to
 * multiple MigrationSteps (see shouldExcludeObjectWithAttributes). This function does not account for that.
 * Prefer using migrationObjectToTab instead.
 */
export const getObjectTypeToTab = lazyInit<{[objectType: string]: MigrationStep}>(() =>
  objectFromEntries(
    getSortedObjectSections().flatMap((objectSectionConfig) =>
      objectSectionConfig.objectTypes.map((objectType: string): [string, MigrationStep] => [
        objectType,
        objectSectionConfig.migrationTab,
      ])
    )
  )
);

export function migrationObjectToTab<T extends ObjectWithAttributes>(object: T): MigrationStep {
  const filteredSections = getSortedObjectSections().filter(
    (objectSectionConfig) =>
      objectSectionConfig.objectTypes.includes(object.objectType as PrimaryObjectType) &&
      !(objectSectionConfig.shouldExcludeObjectWithAttributes?.(object) ?? false)
  );
  if (filteredSections.length !== 1) {
    assertAndContinue(
      false,
      `Expected to find exactly one section for ${object}, but found ${filteredSections.length}: ${filteredSections}`
    );
    return getObjectTypeToTab()[object.objectType];
  }
  return filteredSections[0].migrationTab;
}

/**
 * Given a collection of MigrationObjects, maps them into an array of objects and their relevant
 * ObjectSectionConfigs.
 */
export function generateSections<T extends PrimaryMigrationObject>(
  objects: Array<T>
): Array<{objects: Array<T>; config: ObjectSectionConfig}> {
  const objectsByType = _.groupBy(objects, 'objectType');

  return getSortedObjectSections().flatMap((sectionConfig) => {
    const relevantObjects = sectionConfig.objectTypes
      .flatMap((objectType) => objectsByType[objectType] || [])
      .filter((object) => !(sectionConfig.shouldExcludeObjectWithAttributes?.(object) ?? false));

    if (!relevantObjects.length) {
      return [];
    }
    const objectGroups = sectionConfig.groupRelevantObjects(relevantObjects);
    return objectGroups.map((group) => ({
      objects: group,
      config: sectionConfig,
    }));
  });
}

/** The list of Migration Steps where we do planning (and can filter objects) */
export function getPlanableMigrationSteps(): Array<MigrationStep> {
  return MIGRATION_PLAN_TABS.map((tabConfig) => tabConfig.migrationStep);
}

export function getCurrentAndLaterPlanSteps(currentStep: MigrationStep): Array<MigrationStep> {
  const orderedSteps = getPlanableMigrationSteps();
  const stepIndex = orderedSteps.findIndex((step) => step === currentStep);
  return orderedSteps.slice(stepIndex);
}

function filterMigrationObjects<T extends ObjectWithAttributes>(
  migrationObjects: Array<T>,
  currentStep: MigrationStep,
  includePriorSteps: boolean
): Array<T> {
  const orderedSteps = getPlanableMigrationSteps();
  assertAndContinue(
    orderedSteps.includes(currentStep),
    `Only allow filtering to plannable steps, but was ${currentStep}`
  );
  const currentStepIndex = orderedSteps.findIndex((step) => step === currentStep);
  const validIndices = includePriorSteps
    ? Array.from(Array(currentStepIndex + 1).keys())
    : [currentStepIndex];

  const objectsByType = _.groupBy(migrationObjects, 'objectType');
  return getSortedObjectSections().flatMap((objectSectionConfig) => {
    const sectionStepIndex = orderedSteps.findIndex((step) => step === objectSectionConfig.migrationTab);
    if (!validIndices.includes(sectionStepIndex)) {
      return [];
    }
    const objectsOfRelevantType = objectSectionConfig.objectTypes.flatMap(
      (objectType) => objectsByType[objectType] || []
    );
    return objectsOfRelevantType.filter(
      (object) => !(objectSectionConfig.shouldExcludeObjectWithAttributes?.(object) ?? false)
    );
  });
}

export function filterMigrationObjectsToAllPlannedSteps<T extends ObjectWithAttributes>(
  migrationObjects: Array<T>,
  currentStep: MigrationStep
): Array<T> {
  return filterMigrationObjects(migrationObjects, currentStep, true);
}

export function filterMigrationObjectsToCurrentStep<T extends ObjectWithAttributes>(
  migrationObjects: Array<T>,
  currentStep: MigrationStep
): Array<T> {
  return filterMigrationObjects(migrationObjects, currentStep, false);
}
